// Client-side audio CAPTURE choice — entirely independent of the
// backend-only STT/TTS speech-provider selection (DEFAULT_STT_PROVIDER/
// DEFAULT_TTS_PROVIDER, resolved server-side per meeting and never
// exposed as a frontend control). This enum only controls which
// MediaStream(s) the browser captures before handing normalized PCM to
// the existing WebSocket pipeline.
//
// These are INTERNAL values: they match the backend's MeetingAudioSource
// and are what useAudio/LiveMeetingPage branch on, including for
// historical meetings that were created with `microphone`. What the USER
// can pick is AUDIO_SOURCE_OPTIONS below - a different, smaller thing.
export const AUDIO_SOURCES = {
  MICROPHONE: 'microphone',
  MEETING_TAB: 'meeting_tab',
  MIXED: 'mixed',
};

// What a NEW Web meeting uses: only Meeting / Browser Tab. The UI no longer
// offers a control for it - it is shown as a fixed value.
// That mode captures the shared meeting tab AND still records the
// user's own microphone internally (see useAudio.js / audioClient.js), so
// "Microphone" is no longer offered as a separate mode. The microphone
// pipeline itself is unchanged.
export const FIXED_AUDIO_SOURCE_LABEL = 'Meeting / Browser Tab';

export const AUDIO_SOURCE_OPTIONS = [
  { value: AUDIO_SOURCES.MEETING_TAB, label: FIXED_AUDIO_SOURCE_LABEL },
];

export const DEFAULT_AUDIO_SOURCE = AUDIO_SOURCES.MEETING_TAB;

// Anything that is not a currently selectable value - an old saved
// preference of 'microphone' (or 'mixed'), a missing value, garbage -
// resolves to the default. A read-time fallback, so no stored data has to
// be migrated and the selector can never be empty or invalid.
export function normalizeAudioSource(value) {
  return AUDIO_SOURCE_OPTIONS.some((option) => option.value === value)
    ? value
    : DEFAULT_AUDIO_SOURCE;
}

export function isTabBasedAudioSource(source) {
  return (
    source === AUDIO_SOURCES.MEETING_TAB || source === AUDIO_SOURCES.MIXED
  );
}

// Whether this is a phone or tablet. iPadOS 13+ reports a desktop Mac user
// agent, so a Mac with a touch screen is treated as an iPad (no real Mac
// has one).
export function isPhoneOrTablet() {
  if (typeof navigator === 'undefined') {
    return false;
  }

  if (navigator.userAgentData && navigator.userAgentData.mobile === true) {
    return true;
  }

  if (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '')) {
    return true;
  }

  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

// Whether THIS browser can capture a meeting tab's audio.
//
// Tab capture is getDisplayMedia(), which desktop Chrome/Edge/Firefox
// implement and phone and tablet browsers do not. Checking for the
// function alone is not enough: iOS WebKit (Safari, and every other iPhone
// browser, which all run on it) DEFINES getDisplayMedia but always rejects
// it with NotAllowedError ("The request is not allowed by the user agent or
// the platform in the current context"). So a phone or tablet is ruled out
// first, and the function is checked for everything else.
//
// Deliberately `true` when `navigator.mediaDevices` is missing altogether
// (an insecure page, a test environment): there the microphone fails too,
// with its own clearer message, and answering "no" here would quietly
// change what a meeting is created as for a reason this check cannot see.
export function canCaptureMeetingTab() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
    return true;
  }

  if (isPhoneOrTablet()) {
    return false;
  }

  return typeof navigator.mediaDevices.getDisplayMedia === 'function';
}

// The capture a browser can ACTUALLY perform for a meeting stored with
// `source`.
//
// A tab-based meeting opened in a browser that cannot capture a tab (a
// phone) listens through the microphone instead - the one capture every
// browser has - rather than failing at the first click. Client-side only:
// the meeting's stored `audio_source` is not touched, because the same
// meeting can be continued from a desktop browser, which should go back to
// capturing the tab.
export function resolveCaptureSource(source) {
  return isTabBasedAudioSource(source) && !canCaptureMeetingTab()
    ? AUDIO_SOURCES.MICROPHONE
    : source;
}

// What a NEW meeting created from this browser should be stored as.
export function defaultAudioSourceForDevice() {
  return resolveCaptureSource(DEFAULT_AUDIO_SOURCE);
}
